import type { MeterDirection } from '../../../../utils/smgwUtils';
import type { MeterValueSource } from '../../../../utils/meterRows';
import { PROVENANCE_BADGE_CLASSES, type Provenance } from '../../../../utils/meterProvenance';

/**
 * Messrichtungs-Badge eines Messpunkts: macht Einspeise-Messpunkte (PV &
 * andere Erzeugung) auf einen Blick von Bezugs-Messpunkten unterscheidbar.
 */
export const DirectionBadge = ({ direction }: { direction: MeterDirection }) => (
  <span
    className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${
      direction === 'einspeisung'
        ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
        : 'bg-info-100 dark:bg-info-900/30 text-info-700 dark:text-info-300'
    }`}
    title={direction === 'einspeisung'
      ? 'Einspeise-Messpunkt — misst die ins Netz eingespeiste Erzeugung (z. B. PV) dieses Anschlusses.'
      : 'Bezugs-Messpunkt — misst den Netzbezug dieses Anschlusses.'}
  >
    {direction === 'einspeisung' ? '↑ Einspeisung' : '↓ Bezug'}
  </span>
);

const VALUE_SOURCE: Record<MeterValueSource, { label: string; title: string; classes: string }> = {
  measured: {
    label: 'gemessen · TAF 10',
    title: 'Live-Wert dieses Zählers vom Smart Meter Gateway (TAF 10, Netzzustandsdaten).',
    classes: PROVENANCE_BADGE_CLASSES.measured,
  },
  stale: {
    label: 'letzte Messung',
    title: 'Letzter TAF-10-Wert dieses Zählers — älter als das Live-Fenster, geht nicht in die aktuelle Summe ein.',
    classes: PROVENANCE_BADGE_CLASSES.unknown,
  },
  estimated: {
    label: 'geschätzt',
    title: 'Keine Messung an diesem Zähler: Schätzung aus dem korrigierten Hauswert (Trafo-/Abgangsabgleich) bzw. aus Lastprofil und Jahresverbrauch.',
    classes: PROVENANCE_BADGE_CLASSES.estimated,
  },
  pooled: {
    label: 'im Topf',
    title: 'Am Anschluss gibt es Messwerte, die sich keinem Zähler zuordnen lassen (die Registry liefert die Messlokation nicht mit). Siehe Summe oben.',
    classes: PROVENANCE_BADGE_CLASSES.unknown,
  },
  none: {
    label: 'kein Live-Wert',
    title: 'Für diesen Zähler gibt es keinen aktuellen Wert. Zählerstände (TAF 1/TAF 7) lassen sich beim Messstellenbetreiber abrufen, sofern vorhanden.',
    classes: PROVENANCE_BADGE_CLASSES.unknown,
  },
};

/** Where one meter's current value comes from — next to every value, never implied. */
export const ValueSourceBadge = ({ source }: { source: MeterValueSource }) => {
  const s = VALUE_SOURCE[source];
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${s.classes}`} title={s.title}>
      {s.label}
    </span>
  );
};

/** Provenance of a connection's total value (live: measurement pool input, archive: snapshot meter inputs). */
export const ProvenanceBadge = ({ provenance, className = '' }: { provenance: Provenance; className?: string }) => (
  <span
    className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${PROVENANCE_BADGE_CLASSES[provenance.kind]} ${className}`}
    title={provenance.description}
  >
    {provenance.label}
  </span>
);
